import '@bucketplace/next-extra/patch';
import { StickyProvider } from '@bucketplace/common';
import { type Env, SERVICE_DOMAINS } from '@bucketplace/config';
import { DesignSystemProvider } from '@bucketplace/design-system';
import { LoggerProvider } from '@bucketplace/logkit';
import { init } from '@bucketplace/metadata/sdk';
import { LocationContextProvider } from '@bucketplace/next-extra';
import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { NextPage } from 'next';
import App, { type AppContext, type AppProps } from 'next/app';
import { useRouter } from 'next/router';
import React, {
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { APP_NAME, APP_PROFILE, APP_VERSION, ENV } from '@/common';
import { sessionManager } from '@/common/jwt-auth';
import { DefaultPageLayout, Layout } from '@/components/Layout';
import {
  BrazeProvider,
  GlobalModalProvider,
  SearchQueryContextProvider,
  UserContextProvider,
} from '@/provider';
import { makeTrpcClient } from '@/trpc/client';
import { TRPCProvider } from '@/trpc/utils';
import { initDatadogRum, saveAppOsInfo, setUtmData } from '@/utils';
import { getQueryClient } from '@/utils/query-client';
import 'react-toastify/dist/ReactToastify.css';
import '@/styles/global.css';

init.browser({
  env: APP_PROFILE as Env,
  name: APP_NAME,
  version: APP_VERSION ?? APP_PROFILE,
  serviceType: 'OK',
});
sessionManager.prepare();
initDatadogRum({
  applicationId: '24438129-03ab-46fe-8ec6-ade54e092e66',
  clientToken: 'pubd365e57ef876ac08b73df3e5626ad415',
  service: 'event-web',
  env: APP_PROFILE === 'local' ? 'dev' : APP_PROFILE,
  version: APP_VERSION,
  sessionSampleRate: 1,
  sessionReplaySampleRate: 0,
});

type Page<P = Record<string, never>> = NextPage<P> & {
  getLayout?: (page: ReactNode) => ReactNode;
};

type MyAppProps = AppProps & {
  Component: Page;
};

function getDefaultPageLayout(page: ReactNode): ReactElement {
  return <DefaultPageLayout>{page}</DefaultPageLayout>;
}

function MyApp({ Component, pageProps }: MyAppProps): React.ReactElement {
  const router = useRouter();
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() => makeTrpcClient());

  useEffect(() => {
    setUtmData();
    saveAppOsInfo();

    router.events.on('routeChangeComplete', setUtmData);
    return () => router.events.off('routeChangeComplete', setUtmData);
  }, [router.events]);

  const getLayout = Component.getLayout || getDefaultPageLayout;
  return (
    <LocationContextProvider>
      <LoggerProvider
        initOptions={{
          ingestion: `${SERVICE_DOMAINS.LOGGING[ENV]}/log/v2`,
          batchSize: 50,
          flushIntervalMs: 5000,
          maxRows: 1000,
          maxAgeMs: 3600000,
        }}
        autoFlushOnUnmount
      >
        <DesignSystemProvider mode="light">
          <QueryClientProvider client={queryClient}>
            <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
              <HydrationBoundary state={pageProps.dehydratedState}>
                <SearchQueryContextProvider>
                  <UserContextProvider>
                    <StickyProvider>
                      <GlobalModalProvider>
                        <BrazeProvider>
                          <Layout>
                            {getLayout(<Component {...pageProps} />)}
                          </Layout>
                        </BrazeProvider>
                      </GlobalModalProvider>
                    </StickyProvider>
                  </UserContextProvider>
                </SearchQueryContextProvider>
              </HydrationBoundary>

              <ReactQueryDevtools />
            </TRPCProvider>
          </QueryClientProvider>
        </DesignSystemProvider>
      </LoggerProvider>
    </LocationContextProvider>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);
  return { ...appProps };
};

export default MyApp;
